import ReactMarkdown from 'react-markdown'
import { useLink } from '../contexts/LinkContext'

// Allow our internal entity: scheme through react-markdown's URL sanitizer while
// still blocking dangerous protocols (markdown can be player-authored, e.g. a
// character backstory an admin later views).
function urlTransform(url) {
  if (url.startsWith('entity:')) return url
  if (/^(https?:|mailto:|tel:)/i.test(url)) return url
  if (url.startsWith('#') || url.startsWith('/')) return url
  return ''
}

// Reusable component for rendering markdown text with links.
// Supports: [link text](url), **bold**, *italic*, line breaks, empty lines,
// and [[wiki-links]] / [[Target|Label]] that resolve to in-app navigation.
function MarkdownText({ children, style = {} }) {
  const { resolveLink, openEntity } = useLink()

  if (!children) return null
  const { whiteSpace: _whiteSpace, ...cleanStyle } = style

  // Convert [[Target]] or [[Target|Label]] into a markdown link using a private
  // `entity:` scheme that the `a` renderer below intercepts.
  const withWikiLinks = String(children).replace(
    /\[\[([^\]|]+?)(?:\|([^\]]+?))?\]\]/g,
    (_match, target, label) => {
      const text = (label || target).trim()
      // Also escape parens — encodeURIComponent leaves them, but an unescaped
      // ) would prematurely close the markdown link destination.
      const encoded = encodeURIComponent(target.trim()).replace(/[()]/g, (c) =>
        '%' + c.charCodeAt(0).toString(16)
      )
      return `[${text}](entity:${encoded})`
    }
  )

  // Preprocess text to preserve empty lines between paragraphs
  const processedText = withWikiLinks
    .replace(/\n\n+/g, '\n\n&nbsp;\n\n') // Preserve explicit blank lines for extra spacing
    .replace(/\n/g, '  \n') // Add two spaces before single newlines for markdown line breaks

  return (
    <ReactMarkdown
      urlTransform={urlTransform}
      components={{
        a: ({ href, children }) => {
          // Internal [[wiki-link]]
          if (href && href.startsWith('entity:')) {
            const target = decodeURIComponent(href.slice('entity:'.length))
            const match = resolveLink(target)

            if (match) {
              return (
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault()
                    openEntity(match.type, match.id)
                  }}
                  title={`${match.type}: ${match.label}`}
                  style={{
                    color: 'var(--accent-cyan)',
                    textDecoration: 'underline',
                    textUnderlineOffset: '2px',
                    cursor: 'pointer',
                  }}
                  onMouseOver={(e) => (e.target.style.color = 'var(--accent-teal-light, #4ecdc4)')}
                  onMouseOut={(e) => (e.target.style.color = 'var(--accent-cyan)')}
                >
                  {children}
                </a>
              )
            }

            // No match (typo, or hidden from this player) — show as muted text,
            // not a dead link.
            return (
              <span
                title="No matching entry"
                style={{ color: 'var(--text-muted)', borderBottom: '1px dotted var(--text-muted)' }}
              >
                {children}
              </span>
            )
          }

          // External link (original behavior)
          return (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: 'var(--accent-cyan)',
                textDecoration: 'underline',
                textUnderlineOffset: '2px',
              }}
              onMouseOver={(e) => (e.target.style.color = 'var(--accent-teal-light, #4ecdc4)')}
              onMouseOut={(e) => (e.target.style.color = 'var(--accent-cyan)')}
            >
              {children}
            </a>
          )
        },
        // Style paragraphs with tighter spacing; blank lines create larger gaps
        p: ({ children }) => {
          const text = Array.isArray(children) ? children.join('') : String(children ?? '')
          const isSpacer = text.replace(/ /g, '').trim() === ''

          if (isSpacer) {
            return <span style={{ display: 'block', height: '0.6em' }} aria-hidden="true" />
          }

          return (
            <span style={{ display: 'block', marginBottom: '0.2em', ...cleanStyle }}>
              {children}
            </span>
          )
        },
      }}
    >
      {processedText}
    </ReactMarkdown>
  )
}

export default MarkdownText
